import Head from "next/head"
import Link from "next/link"
import { useRouter } from "next/router"
import { BlitzPage, Routes } from "@blitzjs/next"
import { useQuery, useMutation, invoke } from "@blitzjs/rpc"
import { useState, useEffect, useCallback, ReactNode, useMemo, useRef } from "react"
import { PipelinePage, Prisma } from "db"
import Layout from "app/core/layouts/Layout"
import Suspense from "app/core/components/Suspense"
import PipelineFilters from "app/pipelines/components/PipelineFilters"
import getPipelines from "app/pipelines/queries/getPipelines"
import configurePipelinePages from "app/pipelines/mutations/configurePipelinePages"
import deployPipeline from "app/pipelines/mutations/deployPipeline"
import deletePipeline from "app/pipelines/mutations/deletePipeline"
import copyPipeline from "app/pipelines/mutations/copyPipeline"

import Container from "@mui/material/Container"
import Checkbox from "@mui/material/Checkbox"
import Typography from "@mui/material/Typography"
import IconButton from "@mui/material/IconButton"
import Card from "@mui/material/Card"
import CardHeader from "@mui/material/CardHeader"
import CardMedia from "@mui/material/CardMedia"
import CardActions from "@mui/material/CardActions"
import Button from "@mui/material/Button"
import MuiLink from "@mui/material/Link"
import Tooltip from "@mui/material/Tooltip"
import Box from "@mui/material/Box"
import ToggleButton from "@mui/material/ToggleButton"
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup"
import Badge from "@mui/material/Badge"
import Chip from "@mui/material/Chip"
import Stack from "@mui/material/Stack"
import Paper from "@mui/material/Paper"
import Pagination from "@mui/material/Pagination"
import AddIcon from "@mui/icons-material/Add"
import EditIcon from "@mui/icons-material/Edit"
import DeleteIcon from "@mui/icons-material/Delete"
import CopyIcon from "@mui/icons-material/ContentCopy"
import DesktopWindowsIcon from "@mui/icons-material/DesktopWindows"
import SmartphoneIcon from "@mui/icons-material/Smartphone"
import GitHubIcon from "@mui/icons-material/GitHub"
import RefreshIcon from "@mui/icons-material/Refresh"
import UploadIcon from "@mui/icons-material/Upload"
import GoogleIcon from "@mui/icons-material/Google"

import { subDays } from "date-fns"
import { convertMidnightUTC } from "app/date"

import getPipelinePublicUrl from "app/pipelines/services/getPipelinePublicUrl"
import getPipelineGoogleAnalyticsUrl from "app/pipelines/services/getPipelineGoogleAnalyticsUrl"
import getPageScreenshotUrl, { ScreenshotType } from "app/pipelines/services/getPageScreenshotUrl"
import PipelineBulkActionsProvider, {
  PipelineBulkActions,
} from "app/pipelines/components/PipelineBulkActions"
import usePipelineBulkActions from "app/pipelines/hooks/usePipelineBulkActions"
import { DeploymentStatus } from "app/pipelines/validations"

import CopyDialog from "app/pipelines/components/CopyDialog"
import { Snackbar, SnackbarState } from "app/core/components/Snackbar"
import { normalizeGithubRepositoryPath } from "integrations/github"
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material"
import getPipelineTranslationsDiff from "app/pipelines/queries/getPipelineTranslationsDiff"

import { BADGE_STATUSES } from "../../app/types"
import ErrorMessage from "app/core/components/ErrorMessage"

type PipelinePageType = Pick<PipelinePage, "number" | "key" | "path"> & {
  uniquePageviewsCount: number
  clickthroughRate: number
}

export type PipelineType = Awaited<ReturnType<typeof getPipelines>>["pipelines"][number]

interface PipelinePageCardProps {
  pipeline: PipelineType
  page: PipelinePageType
  screenshotType: ScreenshotType
}

export interface UniquePageviewsConditions {
  uniquePageviewsDateFrom?: Date
  uniquePageviewsDateTo?: Date
}

interface PipelineListProps {
  screenshotType: ScreenshotType
  search: Object
  uniquePageviewsConditions: UniquePageviewsConditions
  setRefetch: React.Dispatch<React.SetStateAction<Function>>
}

interface SelectPipelineCheckboxProps {
  pipeline: PipelineType
}

interface PipelineCardProps {
  pipeline: PipelineType
  screenshotType: ScreenshotType
  refetch: Function
}

interface DeploymentBadgeProps {
  deploymentStatus: PipelineType["deploymentStatus"]
  children: ReactNode
}

const PIPELINES_PER_PAGE = 50

const PipelinePageCard = ({ pipeline, page, screenshotType }: PipelinePageCardProps) => {
  const publicUrl = getPipelinePublicUrl(pipeline)
  const pageUrl = `${publicUrl.toString()}${page.path ? `${page.path}/` : ""}`

  const title = page.path || "index"
  const image = getPageScreenshotUrl(pipeline, page, screenshotType)

  let width: number, height: number
  height = 160
  if (screenshotType === ScreenshotType.DESKTOP) {
    width = height * (1280 / 800)
  } else {
    width = height * (360 / 800)
  }

  return (
    <Card sx={{ mx: 1, flexShrink: 0 }}>
      <CardMedia
        sx={{
          position: "relative",
          width,
          height,
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            background: "white",
          },
        }}
        component="img"
        image={image}
        loading="lazy"
      />
      <CardActions sx={{ width }}>
        <Tooltip title={title}>
          <MuiLink
            href={pageUrl}
            underline="none"
            color="inherit"
            display="block"
            sx={{ fontSize: 14 }}
            noWrap
          >
            <Typography variant="body2" display="block" component="span">
              {title}
            </Typography>
            <Typography variant="caption" display="block" component="span">
              {page.uniquePageviewsCount}
              <br />
              {Math.round(page.clickthroughRate * 100)}%
            </Typography>
          </MuiLink>
        </Tooltip>
      </CardActions>
    </Card>
  )
}

export const DeploymentBadge = ({ deploymentStatus, children }: DeploymentBadgeProps) => {
  const [inProgress, setInProgress] = useState(false)
  const [color, setColor] = useState<"error" | "warning" | "success" | "info">("error")
  const [deploymentBadgeInvisible, setDeploymentBadgeInvisible] = useState(false)

  useEffect(() => {
    const statusesInProgress = [
      DeploymentStatus.IN_PROGRESS,
      DeploymentStatus.PENDING_INVALIDATION,
      DeploymentStatus.INVALIDATION_IN_PROGRESS,
    ]

    setInProgress(statusesInProgress.includes(deploymentStatus as DeploymentStatus))
  }, [deploymentStatus])

  useEffect(() => {
    if (inProgress) {
      setColor(BADGE_STATUSES.WARNING)
    } else {
      switch (deploymentStatus) {
        case DeploymentStatus.FAILED:
        case DeploymentStatus.INVALIDATION_FAILED:
          setColor(BADGE_STATUSES.ERROR)
          break
        case DeploymentStatus.SUCCESS:
          setColor(BADGE_STATUSES.SUCCESS)
          break
        case DeploymentStatus.NOT_DEPLOYED:
          setColor(BADGE_STATUSES.INFO)
          break
        default:
          setColor(BADGE_STATUSES.ERROR)
      }
    }
  }, [deploymentStatus, inProgress])

  useEffect(() => {
    if (!inProgress) {
      setDeploymentBadgeInvisible(false)
      return
    }

    const intervalId = setInterval(() => {
      setDeploymentBadgeInvisible((prev) => !prev)
    }, 300)

    return () => {
      clearInterval(intervalId)
      setDeploymentBadgeInvisible(false)
    }
  }, [inProgress])

  return (
    <Badge color={color} variant="dot" invisible={deploymentBadgeInvisible}>
      {children}
    </Badge>
  )
}

const SelectPipelineCheckbox = ({ pipeline }: SelectPipelineCheckboxProps) => {
  const { selectedPipelines, toggleSelectPipeline } = usePipelineBulkActions()

  return (
    <Checkbox
      checked={!!selectedPipelines.find((p) => p.id === pipeline.id)}
      onChange={() => toggleSelectPipeline(pipeline)}
    />
  )
}

const GithubRepositoryButton = ({ pipeline }) => {
  const { githubRepositoryUrl, githubRepositoryRef, githubRepositoryPath } = pipeline
  if (!githubRepositoryUrl || !githubRepositoryRef) {
    return null
  }

  const url = `${githubRepositoryUrl}/tree/${githubRepositoryRef}/${normalizeGithubRepositoryPath(
    githubRepositoryPath
  )}`

  return (
    <Link href={url}>
      <Tooltip title="Go to GitHub repository">
        <IconButton>
          <GitHubIcon />
        </IconButton>
      </Tooltip>
    </Link>
  )
}

const PipelineCard = ({ pipeline, screenshotType, refetch }: PipelineCardProps) => {
  const publicUrl = getPipelinePublicUrl(pipeline)
  const googleAnalyticsUrl = getPipelineGoogleAnalyticsUrl(pipeline)
  const pipelineName = publicUrl.toString().replace(/^.*:\/\//, "")

  const [configurePipelinePagesMutation, { isLoading: configurePipelinePagesMutationIsLoading }] =
    useMutation(configurePipelinePages)
  const [deployPipelineMutation, { isLoading: deployPipelineMutationIsLoading }] =
    useMutation(deployPipeline)
  const [deletePipelineMutation] = useMutation(deletePipeline)
  const [copyPipelineMutation, { isLoading: copyPipelineMutationIsLoading }] =
    useMutation(copyPipeline)
  const [open, setOpen] = useState(false)
  const [deployIsLoading, setDeployIsLoading] = useState(false)
  const [showPipelineConfirmationDialog, setShowPipelineConfirmationDialog] = useState(false)
  const [translationDiffKeys, setTranslationDiffKeys] = useState<string[]>([])
  const [snackbarData, setSnackbarData] = useState<SnackbarState>({
    type: "success",
    text: "",
    show: false,
  })

  const deploySinglePipeline = async (pipelineId: number) => {
    try {
      await deployPipelineMutation({ id: pipelineId })
      setSnackbarData({ type: "success", text: "Pipeline deployed!", show: true })
    } catch (err) {
      setSnackbarData({ type: "error", text: err.toString(), show: true })
    }
  }

  const deployBtnClicked = async (pipelineId: number) => {
    setDeployIsLoading(true)
    try {
      const data = await invoke(getPipelineTranslationsDiff, pipelineId)
      setDeployIsLoading(false)
      if (data?.isDiff) {
        setTranslationDiffKeys(data.diffKeys)
        setShowPipelineConfirmationDialog(true)
        return
      }
      await deploySinglePipeline(pipelineId)
    } catch (err) {
      setSnackbarData({ type: "error", text: err.toString(), show: true })
      setDeployIsLoading(false)
      return
    }
  }

  return (
    <Card sx={{ mb: 4 }} key={pipeline.id}>
      <CardHeader
        action={
          <>
            <Box sx={{ pl: 1, fontSize: 12 }}>
              Last deployment date: {pipeline.lastDeploymentDate}
            </Box>
            <Box
              sx={{
                textAlign: {
                  md: "right",
                },
              }}
            >
              {googleAnalyticsUrl && (
                <Link href={googleAnalyticsUrl} legacyBehavior>
                  <Tooltip title="Go to Google Analytics dashboard">
                    <IconButton>
                      <GoogleIcon />
                    </IconButton>
                  </Tooltip>
                </Link>
              )}
              <GithubRepositoryButton pipeline={pipeline} />
              <Tooltip title="Deploy pipeline">
                <span>
                  <IconButton
                    disabled={deployPipelineMutationIsLoading || deployIsLoading}
                    onClick={async () => {
                      await deployBtnClicked(pipeline.id)
                    }}
                  >
                    <DeploymentBadge deploymentStatus={pipeline.deploymentStatus}>
                      <UploadIcon />
                    </DeploymentBadge>
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title="Refresh pages">
                <span>
                  <IconButton
                    disabled={configurePipelinePagesMutationIsLoading}
                    onClick={async () => {
                      try {
                        await configurePipelinePagesMutation({ id: pipeline.id })
                        refetch()
                      } catch (err) {
                        setSnackbarData({ type: "error", text: err.toString(), show: true })
                      }
                    }}
                  >
                    <RefreshIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Link href={Routes.EditPipelinePage({ pipelineId: pipeline.id })}>
                <Tooltip title="Edit pipeline">
                  <IconButton>
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              </Link>
              <Tooltip title="Copy pipeline">
                <span>
                  <IconButton
                    disabled={copyPipelineMutationIsLoading}
                    onClick={() => setOpen(true)}
                  >
                    <CopyIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Delete pipeline">
                <IconButton
                  onClick={async () => {
                    const confirmText =
                      "This will be deleted!" +
                      (pipeline.defaultPipes.length > 0 ? " It's used in default pipes" : "")

                    if (window.confirm(confirmText)) {
                      await deletePipelineMutation({ id: pipeline.id })
                      refetch()
                    }
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
              <CopyDialog
                pipeline={pipeline}
                open={open}
                setOpen={setOpen}
                copyPipelineMutation={copyPipelineMutation}
                setSnackbarData={setSnackbarData}
              />
            </Box>
            {showPipelineConfirmationDialog && (
              <Dialog
                fullWidth
                maxWidth="md"
                open={true}
                onClose={() => {
                  setShowPipelineConfirmationDialog(false)
                }}
              >
                <DialogTitle>
                  There are unsaved changes. Save changes and publish translations to deploy?
                </DialogTitle>
                <DialogContent>
                  <DialogContentText>
                    <strong style={{ color: "red" }}>Warning!</strong>
                    <p style={{ color: "red", margin: "0 0 10px 0" }}>
                      Pipeline haven&apos;t been deployed with new translation keys! These keys will
                      have default:
                    </p>
                    {translationDiffKeys?.map((diffKey, index) => {
                      return <div key={`diff-key-modal-${index}`}>{diffKey}</div>
                    })}
                  </DialogContentText>
                </DialogContent>
                <DialogActions>
                  <Button
                    onClick={async () => {
                      setShowPipelineConfirmationDialog(false)
                      setTranslationDiffKeys([])
                      await deploySinglePipeline(pipeline.id)
                    }}
                  >
                    Deploy
                  </Button>
                  <Button
                    onClick={() => {
                      setShowPipelineConfirmationDialog(false)
                      setTranslationDiffKeys([])
                    }}
                  >
                    Cancel
                  </Button>
                </DialogActions>
              </Dialog>
            )}
          </>
        }
        title={
          <Stack direction="row" spacing={3} alignItems="center">
            <SelectPipelineCheckbox pipeline={pipeline} />
            <MuiLink href={publicUrl.toString()} underline="none">
              {pipelineName}
            </MuiLink>
            {pipeline.defaultPipes.length > 0 && (
              <Chip label="default" color="primary" size="small" variant="outlined" />
            )}
          </Stack>
        }
        subheader={
          <>
            <strong>ID{pipeline.id}</strong> ·{" "}
            <strong>{pipeline.pipelinePages.length} pages</strong> · Standard:{" "}
            <strong>{pipeline.standardDisplayPriceString}</strong> · Exit-intent:{" "}
            <strong>{pipeline.exitIntentDisplayPriceString}</strong> · Offer ID:{" "}
            <strong>{pipeline.offerId}</strong> · Paymodes:{" "}
            <strong>{pipeline.paymentMethods.map((item) => item.code).join(", ")}</strong> · Branch:{" "}
            <strong>{pipeline.branch}</strong>
            {pipeline.githubRepositoryPath && (
              <span>
                {" "}
                · Path: <strong>{pipeline.githubRepositoryPath}</strong>
              </span>
            )}
          </>
        }
        sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, rowGap: 1 }}
      />
      <Box sx={{ px: 1, pb: 2, display: "flex", flexDirection: "row", overflow: "scroll" }}>
        {pipeline.pipelinePages
          .sort((a, b) => a.number - b.number)
          .map((page) => (
            <PipelinePageCard
              key={page.number}
              pipeline={pipeline}
              page={page}
              screenshotType={screenshotType}
            />
          ))}
      </Box>
      <Snackbar snackbarData={snackbarData} setSnackbarData={setSnackbarData} />
    </Card>
  )
}

export const PipelinesList = ({
  screenshotType,
  search,
  uniquePageviewsConditions,
  setRefetch,
}: PipelineListProps) => {
  const [page, setPage] = useState(1)
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const handlePageChange = useCallback((_, value: number) => setPage(value), [])

  useEffect(() => {
    setPage(1)
  }, [search])

  const [{ pipelines, count, isError = false }, { refetch }] = useQuery(getPipelines, {
    orderBy: { name: "asc" },
    where: search,
    uniquePageviewsConditions: {
      uniquePageviewsDateFrom:
        uniquePageviewsConditions.uniquePageviewsDateFrom &&
        convertMidnightUTC(uniquePageviewsConditions.uniquePageviewsDateFrom),
      uniquePageviewsDateTo:
        uniquePageviewsConditions.uniquePageviewsDateTo &&
        convertMidnightUTC(uniquePageviewsConditions.uniquePageviewsDateTo),
    },
    take: PIPELINES_PER_PAGE,
    skip: (page - 1) * PIPELINES_PER_PAGE,
  })

  const stableRefetch = useCallback(() => {
    return refetch()
  }, [refetch])

  useEffect(() => {
    setRefetch(() => stableRefetch)
  }, [setRefetch, stableRefetch])

  useEffect(() => {
    const startPolling = () => {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current)
      }

      const pollForChanges = () => {
        pollingTimeoutRef.current = setTimeout(async () => {
          try {
            await stableRefetch()
          } catch (error) {
            console.error("Polling refetch failed:", error)
          }
          pollForChanges()
        }, 30000)
      }

      pollForChanges()
    }

    startPolling()

    // Cleanup function
    return () => {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current)
        pollingTimeoutRef.current = null
      }
    }
  }, [stableRefetch])

  return (
    <>
      {!isError ? (
        pipelines.map((pipeline) => (
          <PipelineCard
            key={pipeline.id}
            pipeline={pipeline}
            screenshotType={screenshotType}
            refetch={stableRefetch}
          />
        ))
      ) : (
        <ErrorMessage />
      )}

      <Paper sx={{ p: 2, mb: 5, display: "flex", justifyContent: "center" }}>
        <Pagination
          shape="rounded"
          page={page}
          count={Math.ceil(count / PIPELINES_PER_PAGE)}
          onChange={handlePageChange}
        />
      </Paper>
    </>
  )
}

const ScreenshotTypeToggleButtonGroup = (props) => {
  const { screenshotType, setScreenshotType, ...other } = props
  const handleScreenshotType = (_event, newScreenshotType: ScreenshotType) => {
    setScreenshotType(newScreenshotType)
  }

  return (
    <ToggleButtonGroup
      size="small"
      value={screenshotType}
      exclusive
      onChange={handleScreenshotType}
      {...other}
    >
      <ToggleButton value={ScreenshotType.MOBILE}>
        <SmartphoneIcon fontSize="small" />
      </ToggleButton>
      <ToggleButton value={ScreenshotType.DESKTOP}>
        <DesktopWindowsIcon fontSize="small" />
      </ToggleButton>
    </ToggleButtonGroup>
  )
}

const PipelinesPage: BlitzPage = () => {
  const [screenshotType, setScreenshotType] = useState(ScreenshotType.MOBILE)
  const [uniquePageviewsConditions, setUniquePageviewsConditions] =
    useState<UniquePageviewsConditions>(() => ({
      uniquePageviewsDateFrom: subDays(new Date(), 30),
      uniquePageviewsDateTo: new Date(),
    }))
  const [search, setSearch] = useState<Prisma.PipelineWhereInput>({})
  const [refetch, setRefetch] = useState(() => () => {}) // default value for callback

  return (
    <>
      <Head>
        <title>Pipelines</title>
      </Head>

      <Container maxWidth={false}>
        <Box
          sx={{
            m: 1,
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <Typography sx={{ my: 4, flexGrow: 1 }} variant="h4" component="h2">
            Pipelines
          </Typography>
          <Box sx={{ display: "flex", flexDirection: { md: "row", xs: "column" }, gap: 2 }}>
            <ScreenshotTypeToggleButtonGroup
              screenshotType={screenshotType}
              setScreenshotType={setScreenshotType}
            />

            <Link href={Routes.NewPipelinePage()} legacyBehavior>
              <Button variant="contained" startIcon={<AddIcon />}>
                Create
              </Button>
            </Link>
          </Box>
        </Box>

        <PipelineBulkActionsProvider>
          <Suspense hideProgressIndicator>
            <PipelineFilters
              search={search}
              onSearch={(search) => {
                setSearch(search)
                refetch()
              }}
            />
            <PipelineBulkActions search={search} />
          </Suspense>

          <Suspense>
            <PipelinesList
              search={search}
              setRefetch={setRefetch}
              uniquePageviewsConditions={uniquePageviewsConditions}
              screenshotType={screenshotType}
            />
          </Suspense>
        </PipelineBulkActionsProvider>
      </Container>
    </>
  )
}

PipelinesPage.authenticate = true
PipelinesPage.getLayout = (page) => <Layout>{page}</Layout>

export default PipelinesPage
